<template>
  <div>
    <LibraryContentPanel
      v-if="currentContentFolderUuid"
      :content-type="CONTENT_TYPES.PRESENTLEVEL"
      :content-last-save="lastUpdateText"
      :content-save-status="contentSaveStatus"
      :hide-save-button="hideSaveButton"
      @presentlevel-removed="onPresentLevelRemoved"
    />

    <SaveToLibraryButton
      class="button-padding"
      v-else-if="saving || !currentContentFolderUuid"
      :content-uuid="contentUuid"
      :content-payload="{}"
      :user-uuid="userUuid"
      :action-permissions="actionPermissions"
      @saved:start="handleSaving"
      @saved:error="handleSaveError"
      @saved:success="handleSaveSuccess"
    />

    <div
      v-else
      class="well well-lg light-well library-panel"
      aria-live="polite">
      <h4 class="library-panel-header"> Personalized For <span class="fs-block">{{ currentContentFolderName }}</span></h4>

      <button
        v-if="removing && !hideSaveButton"
        class="btn btn-danger btn-block"
        disabled
      >
        <i class="fa fa-circle-o-notch fa-spin"/>
        Removing...
      </button>

      <button
        v-else-if="contentSaving && !hideSaveButton"
        class="btn btn-success btn-block"
        disabled
      >
        <i class="fa fa-circle-o-notch fa-spin"/>
        Saving...
      </button>

      <button
        v-else-if="!hideSaveButton"
        class="btn btn-success btn-block"
        disabled
      >
        All Changes Saved
      </button>
      <p v-if="!hideSaveButton" class="text-muted-dark text-center"><small><i class="fa fa-clock-o" aria-hidden="true"/> Last saved {{ lastUpdateText }}</small></p>
      <a
        v-if="canRemoveCurrentContent"
        class="text-danger"
        @click="handleRemoveClick"
        tabindex="0">
        <i class="fa fa-trash" aria-hidden="true"/>
        Remove From Library
      </a>
    </div>

    <transition name="fade">
      <alert
        type="danger"
        class="action-alert text-center"
        :dismissible="true"
        @dismissed="errorMessage = ''"
        v-if="errorMessage"
      >
        {{ errorMessage }}
      </alert>
    </transition>
  </div>
</template>

<script>
import SaveToLibraryButton from '@/components/goals/SaveToLibraryButton'
import { ACTION_TYPES, CONTENT_TYPES, actionPermissions } from '@/util/access_visibility'
import { mapState, mapActions, mapGetters } from 'vuex'
import { savedWhen } from '@/util/time'
import UserAvatar from '@/components/UserAvatar.vue'
import LibraryContentPanel from '@/components/LibraryContentPanel.vue'
import { CONTENT_SAVE_STATES } from '@/util/contentSaveStates'

export default {
  name: 'SaveToLibraryPanel',
  components: {
    LibraryContentPanel,
    UserAvatar: UserAvatar,
    SaveToLibraryButton
  },
  props: {
    contentUuid: {
      type: String,
      default: undefined
    },
    contentType: {
      type: String,
      default: undefined
    },
    contentSaving: {
      type: Boolean,
      default: false
    },
    contentLastSave: {
      type: String,
      default: undefined
    },
    content: {
      type: Object,
      default: () => {}
    },
    hideSaveButton: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      showStudentModal: false,
      student: undefined,
      saving: false,
      removing: false,
      errorMessage: '',
      CONTENT_SAVE_STATES
    }
  },
  computed: {
    CONTENT_TYPES() {
      return CONTENT_TYPES
    },
    ...mapState('user', { userUuid: state => state.user.uuid }),
    ...mapGetters('user', ['authInfo']),
    ...mapGetters('visibility', ['effectsVisibility']),
    ...mapGetters('library', ['libraryFolderMembers', 'libraryFolderOwner', 'isLibraryFolderShared', 'currentContentFolderName', 'currentContentFolderUuid', 'canRemoveCurrentContent']),
    actionPermissions() {
      return actionPermissions(ACTION_TYPES.SAVE, this.contentType, this.content, this.authInfo)
    },
    lastUpdateText() {
      return savedWhen(this.contentLastSave)
    },
    contentSaveStatus() {
      if (this.removing) {
        return CONTENT_SAVE_STATES.REMOVING
      }
      if (this.saving) {
        return CONTENT_SAVE_STATES.SAVING
      }
    }
  },
  methods: {
    ...mapActions('library', ['deleteResource']),
    handleSaving() {
      this.saving = true
    },
    handleSaveError(err) {
      this.saving = false
      this.errorMessage = err
      this.$emit('saved:error', err)
    },
    handleSaveSuccess(resp) {
      this.saving = false
      this.$emit('saved:success', resp)
    },
    handleRemoveClick(evt) {
      evt.preventDefault()
      let contentTypeText = this.contentType
      if (this.contentType === CONTENT_TYPES.PRESENTLEVEL) {
        contentTypeText = 'present levels'
        this.$sendEvent('Clicked Remove Present Levels from Library')
      }
      if (confirm(`Are you sure you want to delete this ${contentTypeText} from your library?`)) {
        this.removeFromLibrary()
      }
    },
    removeFromLibrary() {
      this.removing = true
      this.deleteResource({
        uuid: this.contentUuid,
        type: this.contentType,
        collection: this.currentContentFolderUuid,
      })
        .then(resp => {
          this.removing = false
          this.$emit('removed:success', resp)
        })
        .catch(err => {
          this.removing = false
          this.$emit('removed:error', err)
        })
    },
    onPresentLevelRemoved() {
      this.$emit('removed:success')
    }
  },
  watch: {
    contentSaving(isSaving) {
      this.saving = isSaving
    }
  }
}
</script>

<style lang="scss">
.button-padding {
  padding-top: 10px;
  padding-bottom: 10px;
}

.library-panel {
  background: #f0f5e3;
  color: #708e1d;
  padding-top: 10px;
  padding-bottom: 10px;

  .btn {
    margin-top: 10px;
    padding-top: 10px;
    padding-bottom: 10px;
    margin-bottom: 10px;
  }

  .library-panel-header {
    color: #333333;
    margin-bottom: 20px;
  }

  a {
    cursor: pointer;
  }
}
.muted {
  color: #999;
}
</style>
